import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useId } from 'react';

const PopupContext = createContext(null);

function formatMessage(message) {
  if (typeof message === 'string') return message;
  if (Array.isArray(message)) return message.map(item => typeof item === 'string' ? item : item?.msg || 'Please check the entered details.').join('\n');
  return message?.msg || message?.message || 'Something went wrong. Please try again.';
}

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
};

export const PopupProvider = ({ children }) => {
  const [popup, setPopup] = useState(null);
  const activeRef = useRef(null);
  const queueRef = useRef([]);
  const nextId = useRef(0);

  const close = useCallback((value) => {
    activeRef.current?.resolve(value);
    activeRef.current = queueRef.current.shift() || null;
    setPopup(activeRef.current?.config || null);
  }, []);

  const showPopup = useCallback((config) => {
    return new Promise((resolve) => {
      const entry = { config: { ...config, id: ++nextId.current }, resolve };
      if (activeRef.current) queueRef.current.push(entry);
      else { activeRef.current = entry; setPopup(entry.config); }
    });
  }, []);

  const alert = useCallback((message, options = {}) => {
    const readableMessage = formatMessage(message);
    const type = options.type || detectType(readableMessage);
    return showPopup({
      mode: 'alert',
      title: options.title || getTitleForType(type),
      message: readableMessage,
      type,
      confirmText: options.confirmText || 'OK',
    });
  }, [showPopup]);

  const confirm = useCallback((message, options = {}) => {
    return showPopup({
      mode: 'confirm',
      title: options.title || 'Confirm',
      message: formatMessage(message),
      type: 'warning',
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
    });
  }, [showPopup]);

  const prompt = useCallback((message, options = {}) => {
    return showPopup({
      mode: 'prompt',
      title: options.title || 'Input Required',
      message: formatMessage(message),
      type: 'info',
      defaultValue: options.defaultValue || '',
      placeholder: options.placeholder || '',
      confirmText: options.confirmText || 'Submit',
      cancelText: options.cancelText || 'Cancel',
    });
  }, [showPopup]);

  return (
    <PopupContext.Provider value={{ alert, confirm, prompt }}>
      {children}
      {popup && <PopupModal key={popup.id} popup={popup} onClose={close} />}
    </PopupContext.Provider>
  );
};

function detectType(message) {
  const lower = (message || '').toLowerCase();
  if (lower.includes('success') || lower.includes('saved') || lower.includes('created') || lower.includes('deleted') || lower.includes('uploaded') || lower.includes('updated')) {
    return 'success';
  }
  if (lower.includes('fail') || lower.includes('error') || lower.includes('not valid') || lower.includes('cannot') || lower.includes('could not')) {
    return 'error';
  }
  if (lower.includes('please') || lower.includes('enter') || lower.includes('select') || lower.includes('fill')) {
    return 'warning';
  }
  return 'info';
}

function getTitleForType(type) {
  switch (type) {
    case 'success': return 'Success';
    case 'error': return 'Error';
    case 'warning': return 'Attention';
    default: return 'Notice';
  }
}

const typeConfig = {
  success: {
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    buttonClass: 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500',
  },
  error: {
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    buttonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  },
  warning: {
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    ),
    buttonClass: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
  },
  info: {
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    buttonClass: 'bg-primary hover:bg-primary-dark focus:ring-blue-500',
  },
};

const PopupModal = ({ popup, onClose }) => {
  const [inputValue, setInputValue] = useState(popup.defaultValue || '');
  const config = typeConfig[popup.type] || typeConfig.info;
  const dialogRef = useRef(null);
  const titleId = useId();
  const messageId = useId();

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // A confirmation starts on Cancel so a stray Enter cannot delete data.
    dialogRef.current?.querySelector(popup.mode === 'prompt' ? 'input' : '[data-initial-focus]')?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [popup.mode]);

  const handleConfirm = () => {
    if (popup.mode === 'alert') onClose(true);
    else if (popup.mode === 'confirm') onClose(true);
    else if (popup.mode === 'prompt') onClose(inputValue);
  };

  const handleCancel = () => {
    if (popup.mode === 'alert') onClose(true);
    else if (popup.mode === 'confirm') onClose(false);
    else if (popup.mode === 'prompt') onClose(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && popup.mode === 'prompt' && e.target.tagName === 'INPUT') { e.preventDefault(); handleConfirm(); }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); handleCancel(); }
    if (e.key === 'Tab') {
      const controls = dialogRef.current.querySelectorAll('button:not([disabled]), input:not([disabled])');
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onKeyDown={handleKeyDown}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-popup-overlay"
        onClick={handleCancel}
      />

      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={messageId} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-popup-in overflow-hidden">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-11 h-11 rounded-full ${config.iconBg} ${config.iconColor} flex items-center justify-center`}>
              {config.icon}
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <h3 id={titleId} className="text-lg font-semibold text-gray-900">{popup.title}</h3>
              <p id={messageId} className="mt-2 text-sm text-gray-600 leading-relaxed whitespace-pre-wrap break-words">{popup.message}</p>
            </div>
          </div>

          {popup.mode === 'prompt' && (
            <div className="mt-4 ml-15">
              <input
                type="text"
                aria-labelledby={titleId}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={popup.placeholder}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          {(popup.mode === 'confirm' || popup.mode === 'prompt') && (
            <button
              onClick={handleCancel}
              data-initial-focus
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
            >
              {popup.cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            data-initial-focus={popup.mode === 'alert' ? true : undefined}
            className={`px-5 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${config.buttonClass}`}
          >
            {popup.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupProvider;
